import {
  ButtonType,
  ButtonWidget,
  CloseButtonWidget,
  CompactInlineAlertWidget,
  InlineAlertType,
  TextLinkKind,
  TextLinkWidget
} from '@tableau/widgets';
import React from 'react';

import {FeatureFlags} from '~commonInfra/featureFlags/FeatureFlags';
import {HelpService} from '~commonInfra/services/HelpService';
import {CommonStyles} from '~commonInfra/styles/CommonStyles';
import {
  useGenerativeAICheckRegistrationHook
} from '~src/settings/general-settings/components/sections/tableau-gai/GenerativeAICheckRegistrationApi';
import {
  GenerativeAIRegistrationHookType,
  GenerativeAiRegistrationState
} from '~src/settings/general-settings/components/sections/tableau-gai/GenerativeAIRegistrationApi';

import {Messages} from '../../../../../codegen/Localize';
import {learnMoreStyle} from '../../../../styles/CommonStyles';
import {componentRegistryEntry} from '../../SettingsComponentRegistryFunctions';

export type GenerativeAIRegistrationProps = {
  name?: string;
  testId?: string;
  gaiHook: GenerativeAIRegistrationHookType;
}

export function GenerativeAIRegistration(props: GenerativeAIRegistrationProps) {

const {registrationState, unregisterEnterprise, registerEnterprise} = props.gaiHook;
const {checkRegistrationState, busy, checkRegistration, resetRegistrationCheck}
  = useGenerativeAICheckRegistrationHook();


function getFriendlyDomainName(domain?: string): string {
  try {
    return new URL(registrationState.domain).host;
  } catch {
    return domain;
  }
}

function getCheckState() {

  if (checkRegistrationState.code==null) {

    return <></>;

  } else {

    let code = Number(checkRegistrationState.code);
    let message = Messages.tableauGAISiteSettingsTestNotRegistered();
    let messageType = InlineAlertType.Error;

    if(code===0) {
      message = Messages.tableauGAISiteSettingsTestSuccess();
      messageType = InlineAlertType.Success;
    } else if (code === 1) {
      message = Messages.tableauGAISiteSettingsTestFailed();
      messageType = InlineAlertType.Warning;
    }

    return <p style={{paddingBottom: 0, marginBottom: 0}}>
      <CompactInlineAlertWidget
        testId={'tableau-ai-success'}
        style={{padding: CommonStyles.Sizes.MediumSpace}}
        type={messageType}>
        <div style={{display: 'flex'}}>
          <div style={{width: '90%', float: 'left'}}>{message}</div>
          <div style={{width: '10%', float: 'right', textAlign: 'right'}}>
            <CloseButtonWidget  onClick={() => resetRegistrationCheck()}/>
          </div>
        </div>
      </CompactInlineAlertWidget>
    </p>;
  }
}

function registrationDetails(registrationState: GenerativeAiRegistrationState) {
  const tableauGAIConnectHelpUrl = HelpService.getHelpUrlByCategory('gpt-salesforce-login');

  return registrationState.registered ? <>
      <div id="tableau-gai-customer-id">
        <p style={{display: 'inline'}}>{Messages.tableauGAISiteSettingsConnected()}</p>
        <TextLinkWidget
          className={learnMoreStyle}
          href={tableauGAIConnectHelpUrl}
          target="_blank"
          testId={'tableau-gai-connect-learn-more'}
        >
          {Messages.learnMore()}
        </TextLinkWidget>
      </div>
      <p style={{display: 'table'}}>
        <table style={{backgroundColor: '#f7f5f6', paddingBottom: CommonStyles.Sizes.MediumSpace, paddingTop: CommonStyles.Sizes.MediumSpace}}>
          <tbody>
            <tr>
              <td style={{width: '100%', paddingLeft: CommonStyles.Sizes.MediumSpace}}>
                <div style={{float: 'right', whiteSpace: 'nowrap'}}>
                  {Messages.tableauGAISiteSettingsSalesforceOrg()}
                </div>
                <div style={{float: 'right', whiteSpace: 'nowrap'}}>
                  {Messages.tableauGAISiteSettingsUsername()}
                </div>
              </td>
              <td style={{width: '100%', alignContent: 'left'}}>
                <div>
                  <TextLinkWidget
                    href={registrationState.domain}
                    kind={TextLinkKind.Standard}
                    target="_blank"
                    testId="link-to-salesforce-instance">{getFriendlyDomainName(registrationState.domain)}
                  </TextLinkWidget>
                </div>
                <div
                   data-tb-test-id={'username-salesforce-connect'}>{registrationState.username}
                </div>
              </td>
              <td style={{whiteSpace: 'nowrap', paddingRight: CommonStyles.Sizes.MediumSpace, paddingLeft: CommonStyles.Sizes.MediumSpace}}>
                <ButtonWidget buttonType={ButtonType.Outline}
                              testId="update-to-salesforce-button"
                              className="tb-outline-button tb-margin-left-narrow"
                              onClick={() => registerEnterprise()}
                              type="button">{Messages.tableauGAISiteSettingsActionEditButton()}
                </ButtonWidget>
                <ButtonWidget buttonType={ButtonType.Outline}
                              style={{marginLeft: CommonStyles.Sizes.MediumSpace}}
                              testId="delete-to-salesforce-button"
                              className="tb-outline-button tb-margin-left-narrow"
                              onClick={() => checkRegistration()}
                              type="button">
                              {busy ? (<span className='tb-icon-spinner tb-lr-padded-narrow' />) : null}
                              <span>{Messages.tableauGAISiteSettingsActionTestButton()}</span>
                </ButtonWidget>
                <ButtonWidget buttonType={ButtonType.Outline}
                              style={{marginLeft: CommonStyles.Sizes.MediumSpace}}
                              testId="delete-to-salesforce-button"
                              className="tb-outline-button tb-margin-left-narrow"
                              onClick={() => unregisterEnterprise()}
                              type="button">{Messages.tableauGAISiteSettingsActionDeleteButton()}
                </ButtonWidget>
              </td>
            </tr>
          </tbody>
        </table>
        {getCheckState()}
      </p>
    </>
    :
    <>
      <div id="tableau-gai-customer-id">
        <p style={{display: 'inline'}}>{Messages.tableauGAISiteSettingsConnect()}</p>
        <TextLinkWidget
          className={learnMoreStyle}
          href={tableauGAIConnectHelpUrl}
          target="_blank"
          testId={'tableau-gai-connect-learn-more'}
        >
          {Messages.learnMore()}
        </TextLinkWidget>
      </div>
      <div style={{marginTop: CommonStyles.Sizes.MediumSpace}}>
        <ButtonWidget buttonType={ButtonType.Outline}
                      testId="connect-to-salesforce-button"
                      className="tb-outline-button tb-margin-left-narrow"
                      onClick={() => registerEnterprise()}
                      type="button">{Messages.tableauGAISiteSettingsActionConnectButton()}
        </ButtonWidget>
      </div>
    </>;
}

  return FeatureFlags.tabGAITokenServiceEnterpriseRegistration ? registrationDetails(registrationState) : null;
}

export const GenerativeAIRegistrationSectionSpecs = componentRegistryEntry({
  name: 'GenerativeAIRegistrationSection',
  component: GenerativeAIRegistration
});
